import { useEffect, useRef, useState, type ReactNode } from 'react';
import type Hls from 'hls.js';
import { feedIsLive, nextPlaylist } from '../lib/playlist-feed.ts';
import { applyQuality, qualityLevels, savedQuality, saveQuality, type QualityChoice } from '../lib/quality.ts';
import { PlayerControls, type PlayerEngine, type QualityState } from './PlayerControls.tsx';

const frameClocks = new WeakMap<HTMLVideoElement, () => number | null>();

/**
 * Wall-clock time (epoch ms) of the frame on screen in the page's player, from the playlist's
 * EXT-X-PROGRAM-DATE-TIME. Reports send it so moderation reviews the moment the viewer saw.
 */
export function frameOnScreenAt(): number | null {
  const el = document.querySelector('video');
  return el ? (frameClocks.get(el)?.() ?? null) : null;
}

/**
 * HLS player. hls.js where Media Source Extensions exist, native HLS otherwise (iPhone Safari).
 * Tries to autoplay with sound, falls back to muted autoplay with a tap-to-unmute prompt.
 * ZIP2LIVE's own controls (PlayerControls) replace the browser's; ?controls=native brings those back.
 */
export function Player({ src, live, streamId, poster, immersive = false, bottomInset, onAspect, title, badges, overlay }: {
  src: string;
  live: boolean;
  streamId?: string;
  /** Picture shown until the video plays (replays: the stream's latest preview frame). */
  poster?: string | null;
  /** Fill the whole screen (portrait streams on phones). */
  immersive?: boolean;
  /** In that layout, how much of the bottom the page's own bar covers. */
  bottomInset?: number;
  /** Width / height of the decoded video, reported whenever it changes. */
  onAspect?: (ratio: number) => void;
  /** The stream's title, shown along the top in full screen. */
  title?: string;
  /** LIVE / REPLAY badge and viewer count, over the top left (and kept in full screen). */
  badges?: ReactNode;
  /** Drawn over the picture, under the controls (floating reactions). */
  overlay?: ReactNode;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const engine = useRef<PlayerEngine>({ hls: null, behindLive: () => null, goLive: () => undefined });
  const [muted, setMuted] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(true);
  const [quality, setQuality] = useState<QualityState | null>(null);
  const [nativeControls] = useState(() => new URLSearchParams(location.search).get('controls') === 'native');

  useEffect(() => {
    const el = video.current;
    if (!el) return;
    let hls: Hls | null = null;
    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const start = async () => {
      try {
        el.muted = false;
        await el.play();
        setMuted(false);
      } catch {
        try {
          el.muted = true;
          await el.play();
          setMuted(true);
        } catch {
          // No autoplay at all (Low Power Mode): the big play button waits for a tap, with sound.
          el.muted = false;
        }
      }
    };

    /**
     * hls.js everywhere it can run (Chrome, Firefox, Edge, desktop Safari, iOS 17.1+ through
     * ManagedMediaSource): it catches up to live, while Safari's native player sits 3 target
     * durations back and never recovers after a stall. Browser broadcasts are packaged as MPEG-TS,
     * so mid-stream resolution changes work here too. Native HLS is the fallback (older iPhones).
     */
    const nativeHls = Boolean(el.canPlayType('application/vnd.apple.mpegurl'));

    // How far behind live the viewer is (paused, or after stalls): the delay behind the camera
    // (data-latency, below) over the smallest seen while playing. While paused the clock counts
    // instead: hls.js's own figures, and the frame's date, stop moving with the picture.
    let bestLatency = Infinity;
    let paused: { at: number; latency: number } | null = null;
    const onPause = () => (paused = { at: Date.now(), latency: Number(el.dataset.latency) || 0 });
    const onPlaying = () => (paused = null);
    el.addEventListener('pause', onPause);
    el.addEventListener('playing', onPlaying);
    const behindLive = () => {
      const latency = paused ? paused.latency + (Date.now() - paused.at) / 1000 : Number(el.dataset.latency);
      if (!live || !(latency > 0)) return null;
      if (!paused) bestLatency = Math.min(bestLatency, latency);
      return Math.max(0, latency - bestLatency);
    };

    // A frame the browser can't decode (a broadcaster's packet loss, say) stops the <video> for
    // good, and hls.js only logs it. Rebuild the decoder (unless hls.js already has, after the
    // MediaSource closed) and play on; the same spot failing again is skipped (live: to the edge).
    let resumeAfterError = false;
    let lastDecodeError = 0;
    const onMediaError = () => {
      const player = hls;
      if (!player || !el.error) return;
      el.dataset.hlsError = `media:${el.error.code}:${el.error.message.slice(0, 60)}`;
      const at = el.currentTime;
      const again = Date.now() - lastDecodeError < 10_000;
      lastDecodeError = Date.now();
      resumeAfterError = true;
      setTimeout(() => {
        if (cancelled || hls !== player) return;
        if (el.error) player.recoverMediaError();
        if (again) player.startLoad(live ? -1 : at + 3);
      }, 1000);
    };
    const onCanPlay = () => {
      if (!resumeAfterError || !el.paused) return;
      resumeAfterError = false;
      void el.play().catch(() => undefined);
    };
    el.addEventListener('error', onMediaError);
    el.addEventListener('canplay', onCanPlay);

    const playNative = () => {
      engine.current = {
        hls: null,
        behindLive,
        goLive: () => {
          el.src = src;
          void el.play().catch(() => undefined);
        },
      };
      el.src = src;
      el.addEventListener('loadedmetadata', () => void start(), { once: true });
      el.addEventListener('playing', () => {
        setWaiting(false);
        setProblem(null);
      });
      el.addEventListener('error', () => {
        setProblem('Reconnecting to the stream…');
        retryTimer = setTimeout(() => {
          el.src = src;
          void el.play().catch(() => undefined);
        }, 2000);
      });
    };

    void (async () => {
      // ?player=native forces Safari's own player (testing and support).
      if (new URLSearchParams(location.search).get('player') === 'native' && nativeHls) return playNative();
      const { default: HlsCtor } = await import('hls.js/light');
      if (cancelled) return;
      if (!HlsCtor.isSupported()) {
        if (nativeHls) return playNative();
        setProblem('This browser cannot play live video.');
        return;
      }
      let mediaRecoveries = 0;
      const attach = () => {
        // Live playlists come from the WebSocket push feed when it is flowing: the loader hands
        // hls.js each new playlist the moment it is pushed (holding the request until then, like
        // an LL-HLS blocking reload) and falls back to a normal network fetch otherwise.
        const DefaultLoader = HlsCtor.DefaultConfig.loader;
        const feedId = live ? streamId : undefined;
        // Streams have two qualities: src (index.m3u8) lists hi.m3u8, the stream as sent, and
        // lo.m3u8, a lighter 360p copy, and hls.js switches between them from how fast segments
        // download. Only hi.m3u8 is pushed; lo.m3u8 is polled. src itself is always fetched: it may
        // be that list of qualities, or (older streams) the only playlist, which is pushed after
        // its first load.
        const playlistPath = new URL(src, location.href).pathname;
        const hiPath = playlistPath.replace(/[^/]*$/, 'hi.m3u8');
        class PushedPlaylistLoader extends DefaultLoader {
          private pending: { aborted: boolean; onAbort?: () => void } | null = null;
          override load(context: Parameters<InstanceType<typeof DefaultLoader>['load']>[0], config: Parameters<InstanceType<typeof DefaultLoader>['load']>[1], callbacks: Parameters<InstanceType<typeof DefaultLoader>['load']>[2]) {
            const path = new URL(context.url, location.href).pathname;
            const isLivePlaylist = feedId && (path === hiPath || (path === playlistPath && context.type !== 'manifest'));
            if (!isLivePlaylist || !feedIsLive(feedId, 4000)) return super.load(context, config, callbacks);
            const signal = { aborted: false as boolean, onAbort: undefined as (() => void) | undefined };
            this.pending = signal;
            const start = performance.now();
            void nextPlaylist(feedId, 3000, signal).then((p) => {
              if (signal.aborted) return;
              this.pending = null;
              if (!p) return super.load(context, config, callbacks);
              const now = performance.now();
              const stats = this.stats;
              stats.loading.start = start;
              stats.loading.first = now;
              stats.loading.end = now;
              stats.loaded = stats.total = p.text.length;
              callbacks.onSuccess({ url: context.url, data: p.text }, stats, context, null);
            });
          }
          override abort() {
            if (this.pending) {
              this.pending.aborted = true;
              this.pending.onAbort?.();
              this.pending = null;
            }
            super.abort();
          }
        }
        hls = new HlsCtor({
          pLoader: PushedPlaylistLoader as any,
          // Not LL-HLS (no parts): this only enables hls.js's gentle catch-up toward targetLatency.
          lowLatencyMode: true,
          liveSyncDuration: 3.5,
          liveMaxLatencyDuration: 9,
          maxLiveSyncPlaybackRate: 1.1,
          backBufferLength: 30,
          manifestLoadingMaxRetry: 6,
          levelLoadingMaxRetry: 6,
          fragLoadingMaxRetry: 6,
          // Quality switching. A live viewer holds only a few seconds of video, so react to a
          // slowing connection faster than hls.js's defaults (3 s / 9 s averages, 95% of the
          // estimate) and move down while there is still some left.
          abrEwmaFastLive: 1.5,
          abrEwmaSlowLive: 5,
          abrBandWidthFactor: 0.8,
          // The playlist's stated bitrates are estimates (an OBS stream is whatever OBS sends), so
          // let what segments actually weigh count too.
          abrMaxWithRealBitrate: true,
          // A quality that failed (most often one frame of the broadcaster's video lost on the way
          // in, which breaks the stream-copied high quality but not the re-encoded 360p) is skipped
          // by Auto until this long has passed. hls.js's default is forever: one bad frame left
          // every viewer on 360p for the rest of the stream.
          errorPenaltyExpireMs: 15_000,
        });
        const player = hls;
        engine.current = {
          hls: player,
          behindLive,
          goLive: () => {
            const edge = player.liveSyncPosition;
            if (edge !== null) el.currentTime = edge;
            void el.play().catch(() => undefined);
          },
        };
        hls.on(HlsCtor.Events.MANIFEST_PARSED, () => {
          // The viewer's saved Auto / High / Low choice (lib/quality.ts), before the first segment.
          if (hls) applyQuality(hls, savedQuality());
          // The settings menu offers the choice only when the stream has both qualities.
          setQuality(hls && qualityLevels(hls) ? { choice: savedQuality(), playing: null } : null);
          void start();
        });
        // Sit as close to live as the stream's chunk size safely allows: 2 s behind for 1 s
        // chunks (2.5 s on phones), 3.5 s for 2 s chunks. Pushed playlists make new chunks known
        // at once, so 2 chunks of margin is enough; a viewer who stalls gets +1 chunk from hls.js
        // (liveSyncOnStallIncrease). Production test: 2.0 s cut ~0.5 s with no extra stalls versus
        // 2.5 s on the same stream (tests/e2e/target-ab.mjs). Set only on change: the setter resets
        // hls.js's stall back-off.
        let lastTarget = 0;
        hls.on(HlsCtor.Events.LEVEL_UPDATED, (_e, data) => {
          if (!data.details.live) return;
          const td = data.details.targetduration;
          // ?target=<seconds> overrides the delay target (latency experiments only).
          const forcedTarget = Number(new URLSearchParams(location.search).get('target')) || 0;
          const floor = matchMedia('(pointer: coarse)').matches ? 2.5 : 2;
          const target = forcedTarget || Math.max(floor, 1.5 * td + 0.5);
          if (target !== lastTarget && hls) {
            lastTarget = target;
            // After a long stall hls.js only creeps back at 1.1x when it is already close to the
            // target, so jump back to live once we are this far behind (kept above the target, or
            // hls.js would keep seeking; the setter does not re-validate it).
            hls.config.liveMaxLatencyDuration = Math.max(6, target + 3 * td);
            hls.targetLatency = target;
          }
        });
        // Which quality is playing, readable from devtools and tests as <video data-quality="hi|lo">.
        hls.on(HlsCtor.Events.LEVEL_SWITCHED, (_e, data) => {
          el.dataset.quality = /\/lo\.m3u8(\?|$)/.test(hls?.levels[data.level]?.uri ?? '') ? 'lo' : 'hi';
          const playing = el.dataset.quality as 'hi' | 'lo';
          setQuality((q) => (q ? { ...q, playing } : q));
        });
        hls.on(HlsCtor.Events.FRAG_BUFFERED, () => {
          setProblem(null);
          setWaiting(false);
        });
        hls.on(HlsCtor.Events.ERROR, (_e, data) => {
          // Last error, readable from devtools/tests: <video data-hls-error="...">.
          el.dataset.hlsError = `${data.fatal ? 'fatal' : 'warn'}:${data.type}:${data.details}`;
          if (data.details === HlsCtor.ErrorDetails.BUFFER_STALLED_ERROR) el.dataset.stalls = String(Number(el.dataset.stalls ?? 0) + 1);
          // hls.js rebuilds the MediaSource itself here; play on afterwards unless the viewer had paused.
          if (data.details === HlsCtor.ErrorDetails.MEDIA_SOURCE_REQUIRES_RESET && (!el.paused || Date.now() - lastDecodeError < 3000)) resumeAfterError = true;
          if (!data.fatal) return;
          if (data.type === HlsCtor.ErrorTypes.MEDIA_ERROR && mediaRecoveries++ < 2) {
            hls?.recoverMediaError();
            return;
          }
          // Network trouble or media that will not recover in place: rebuild the player.
          setProblem('Reconnecting to the stream…');
          hls?.destroy();
          hls = null;
          engine.current = { ...engine.current, hls: null };
          retryTimer = setTimeout(() => {
            if (cancelled) return;
            mediaRecoveries = 0;
            attach();
          }, 2000);
        });
        hls.loadSource(src);
        hls.attachMedia(el);
      };
      attach();
    })();

    // Glass-to-glass estimate from the playlist's EXT-X-PROGRAM-DATE-TIME, readable from devtools
    // and latency probes as <video data-latency="seconds">.
    let farBehind = 0;
    const playingDate = () => {
      const nativeStart = (el as HTMLVideoElement & { getStartDate?: () => Date }).getStartDate?.();
      return hls?.playingDate ?? (nativeStart && !Number.isNaN(nativeStart.getTime()) ? new Date(nativeStart.getTime() + el.currentTime * 1000) : null);
    };
    frameClocks.set(el, () => playingDate()?.getTime() ?? null);
    const latencyTimer = setInterval(() => {
      const playing = playingDate();
      if (!playing) return;
      const latency = (Date.now() - playing.getTime()) / 1000;
      el.dataset.latency = latency.toFixed(2);
      // Safari's native player never catches up after a stall. If a live stream has drifted far
      // behind (normal is ~4-8 s), reload it: Safari restarts at its usual distance from live.
      if (!hls && live && !el.paused) {
        farBehind = latency > 14 ? farBehind + 1 : 0;
        if (farBehind >= 3) {
          farBehind = 0;
          el.src = src;
          void el.play().catch(() => undefined);
        }
      }
    }, 1000);

    return () => {
      cancelled = true;
      el.removeEventListener('pause', onPause);
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('error', onMediaError);
      el.removeEventListener('canplay', onCanPlay);
      clearInterval(latencyTimer);
      if (retryTimer) clearTimeout(retryTimer);
      hls?.destroy();
    };
  }, [src, live]);

  // Report the picture's shape (portrait streams get the full screen on phones). `resize` fires
  // whenever the decoded size changes, including the first frame.
  const onAspectRef = useRef(onAspect);
  onAspectRef.current = onAspect;
  useEffect(() => {
    const el = video.current;
    if (!el) return;
    const report = () => {
      if (el.videoWidth && el.videoHeight) onAspectRef.current?.(el.videoWidth / el.videoHeight);
    };
    el.addEventListener('resize', report);
    el.addEventListener('loadedmetadata', report);
    return () => {
      el.removeEventListener('resize', report);
      el.removeEventListener('loadedmetadata', report);
    };
  }, []);

  // Sound turned on from the controls or the device clears the tap-to-unmute prompt.
  useEffect(() => {
    const el = video.current;
    if (!el) return;
    const onVolume = () => {
      if (!el.muted) setMuted(false);
    };
    el.addEventListener('volumechange', onVolume);
    return () => el.removeEventListener('volumechange', onVolume);
  }, []);

  return (
    <div ref={box} data-player className={`relative w-full overflow-hidden bg-black ${immersive ? 'h-full' : 'sm:rounded-2xl'}`}>
      <video
        ref={video}
        className={`w-full bg-black object-contain ${immersive ? 'h-full' : 'aspect-video'}`}
        poster={poster ?? undefined}
        playsInline
        controls={nativeControls}
        autoPlay
        aria-label={live ? 'Live video' : 'Replay'}
        onPlaying={() => setWaiting(false)}
      />
      {overlay}
      {nativeControls ? (
        badges && <div className="pointer-events-none absolute top-3 left-3 flex items-center gap-2 sm:top-4 sm:left-4">{badges}</div>
      ) : (
        <PlayerControls
          video={video}
          root={box}
          engine={engine}
          live={live}
          immersive={immersive}
          bottomInset={bottomInset}
          title={title}
          badges={badges}
          quality={quality}
          onQuality={(choice: QualityChoice) => {
            saveQuality(choice);
            setQuality((q) => (q ? { ...q, choice } : q));
            if (engine.current.hls) applyQuality(engine.current.hls, choice);
          }}
          loading={waiting && !problem}
          problem={problem}
          autoplayMuted={muted}
          onUnmute={() => setMuted(false)}
        />
      )}
    </div>
  );
}
